import type { InboxSort, InboxView } from "./inbox-model";

/** What the weighted queue says about one waiting chat (`GET /v1/routing/live-chat/queue`). */
export interface QueueOrderEntry {
  position: number;
  score: number;
  waited_s: number;
  rg: boolean;
}

/** The queue as the inbox holds it: the entries by chat id and the time they were read. */
export interface QueueInfo {
  entries: ReadonlyMap<string, QueueOrderEntry>;
  fetchedAt: number;
}

export function queueEntriesOf(items: readonly (QueueOrderEntry & { conversation_id: string })[]): ReadonlyMap<string, QueueOrderEntry> {
  return new Map(items.map((item) => [item.conversation_id, { position: item.position, score: item.score, waited_s: item.waited_s, rg: item.rg }]));
}

/** The queue order replaces the plain newest-first order only in the "Needs staff" view (the chats waiting for a person) and only on the default sort. */
export function ordersByQueue(view: InboxView, sort: InboxSort): boolean {
  return view === "needs_staff" && sort === "recent";
}

/**
 * Waiting chats first, in the order the queue serves them (a required handover, then the highest score with aging); chats the queue
 * does not know keep their place after them. Stable: nothing the queue does not rank moves relative to the others.
 */
export function orderByQueue<T extends { id: string }>(items: readonly T[], entries: ReadonlyMap<string, QueueOrderEntry>): T[] {
  const position = (item: T): number => entries.get(item.id)?.position ?? Number.POSITIVE_INFINITY;
  const queued = items.filter((item) => entries.has(item.id)).sort((a, b) => position(a) - position(b));
  return [...queued, ...items.filter((item) => !entries.has(item.id))];
}

/** Seconds a chat has waited now: what the API said when it was read, plus the time since. */
export function waitedSince(entry: Pick<QueueOrderEntry, "waited_s">, fetchedAt: number, now: number): number {
  return entry.waited_s + Math.max(0, Math.floor((now - fetchedAt) / 1000));
}

/** `m:ss`, or `h:mm:ss` from an hour. */
export function clock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, "0");
  return hours > 0 ? `${String(hours)}:${String(minutes).padStart(2, "0")}:${rest}` : `${String(minutes)}:${rest}`;
}
